import {useCallback} from 'react';

import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import {isSectionPanelsAuto} from '../../../state/views/compress';
import {panelIsActive} from '../../../util/panelbank';
import {PANEL_BANK_HIDDEN_SECTION_NAME} from '../../../util/panelbankTypes';
import {useKeyTypes} from '../../../util/runhelpers';
import {useKeyInfoQueryContext} from '../../MultiRunWorkspace/KeyInfoQueryContext';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {useWorkspaceSettingsContext} from '../../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {type SectionWithRef} from '../types';

/**
 * This hook determines if a section should be visible in the workspace based
 * on search and filtering criteria. If a section should not be rendered
 * by default, that should be handled outside of the PanelBankSection component.
 */
export const useIsSectionVisible = () => {
  const {workspaceLayout} = useWorkspaceSettingsContext();

  const {isSingleRun} = useSingleRunModeContext();
  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();

  const keyTypes = useKeyTypes(historyKeyInfo.keys);
  const {debouncedSearchRegex: searchRegex, isSearching} =
    usePanelSearchDebouncedContext();

  // Note: order of checks matter here!
  return useCallback(
    (sectionWithRef: SectionWithRef) => {
      // showEmptySections is a user workspace setting that applies ONLY when user is doing a **search**
      const shouldShowEmptySectionsDuringSearch =
        workspaceLayout.showEmptySections && isSearching;

      if (shouldShowEmptySectionsDuringSearch) {
        return true;
      }

      // Edge case: sections with no panels might be newly-created by the user.
      // We want to show these sections so panels can be dragged into them
      if (
        sectionWithRef.panels.length === 0 &&
        !isSectionPanelsAuto(sectionWithRef, workspaceLayout)
      ) {
        return true;
      }

      return sectionWithRef.panels.some(panel =>
        panelIsActive({
          section: sectionWithRef,
          panel,
          isSingleRun,
          historyKeyInfo,
          keyTypes,
          searchRegex, // THIS IS IMPORTANT - if we're not searching, this will be undefined and not check against it. If we are searching, it'll be populated and we check if it's both active and a search match.
        })
      );
    },
    [
      historyKeyInfo,
      isSearching,
      isSingleRun,
      keyTypes,
      searchRegex,
      workspaceLayout,
    ]
  );
};

export const getIsHiddenPanelsSection = ({
  idx,
  numSections,
  sectionName,
}: {
  /**
   * Index of the section in the panel bank
   */
  idx: number;
  /**
   * Total number of sections that could be rendered
   */
  numSections: number;
  sectionName: string;
}) => {
  const isLastSection = idx === numSections - 1;
  const isHiddenPanelsSection =
    isLastSection && sectionName === PANEL_BANK_HIDDEN_SECTION_NAME;

  return isHiddenPanelsSection;
};
